import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { Button } from '@peacecover/component';
import { ArrowLeft } from '@peacecover/icon';
import { useGetCastRelationships } from '../../domains/scene/ui/hooks/useGetCastRelationships';
import { useGetBettieBalances } from '../../domains/bettie/ui/hooks/useGetBettieBalances';
import { useHandleHistoryBack } from '../../utils/useHandleHistoryBack';
import PreviousCastRelationshipList from './components/PreviousCastRelationshipList';
import CurrentCastRelationshipSection, {
  DoubleArrow
} from './components/CurrentCastRelationshipSection';
import GradientLoadingSpinner from '../components/GradientLoadingSpinner';
import CreateCastRelationshipBottomSheet from './components/CreateCastRelationshipBottomSheet';
import { ROUTES } from '../../constants/routes';

const CastRelationshipsPage = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: castRelationships, isLoading } = useGetCastRelationships({ page: 1, perPage: 10 });
  const { data: bettieBalances } = useGetBettieBalances();

  const hasCastRelationshipTicket =
    bettieBalances?.castRelationshipTicket?.balance &&
    bettieBalances?.castRelationshipTicket?.balance > 0;

  const handleCreateCastRelationship = () => {
    if (!hasCastRelationshipTicket) {
      setIsOpen(true);
      return;
    }
    navigate(ROUTES.SCENE.SETTING);
  };

  useEffect(() => {
    if (castRelationships?.length === 1) {
      setIsOpen(true);
    }
  }, [castRelationships?.length]);
  

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      const message = event.data as { type?: string } | undefined;
      if (message?.type !== 'PAYMENT_SUCCESS') return;

      queryClient.invalidateQueries(['bettieBalances']);
      navigate(ROUTES.SCENE.SETTING);
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [navigate, queryClient]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background-dark">
        <GradientLoadingSpinner size={36} />
      </div>
    );
  }

  const currentCastRelationship = castRelationships?.[0];

  return (
    <div className="min-h-screen pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] bg-background-dark">
      <Header />
      <div className="px-[24px] py-[10px] mt-[10px] flex flex-col gap-[14px] items-start">
        <h1 className="text-[26px] font-bold leading-[30px] text-main-dark-1 whitespace-pre-line">
          {t('CastRelationship.title')}
        </h1>
        <p className="text-[15px] font-medium leading-[18px] text-main-dark-4">
          {t('CastRelationship.description')}
        </p>
      </div>
      <div className="mt-[33px]">
        <CurrentCastRelationshipSection
          currentCastRelationship={currentCastRelationship ?? undefined}
        >
          <div className="flex flex-col items-center gap-[12px]">
            <DoubleArrow />
            <div className="px-[8px] py-[4px] bg-[linear-gradient(47deg,rgba(69,220,174,0.30)_11.79%,rgba(93,62,250,0.30)_99.08%)] rounded-[30px] text-[11px] font-semibold leading-[13px]">
              <span className="bg-[linear-gradient(47deg,#45DCAE_0%,#5D3EFA_99.08%)] bg-clip-text text-transparent">
                {t('CastRelationship.current')}
              </span>
            </div>
          </div>
        </CurrentCastRelationshipSection>
      </div>
      {castRelationships?.length && castRelationships?.length > 1 ? (
        <PreviousCastRelationshipList
          castRelationships={castRelationships?.slice(1)}
          handleCreateCastRelationship={handleCreateCastRelationship}
        />
      ) : (
        <EmptyCase />
      )}
      <CreateCastRelationshipBottomSheet isOpen={isOpen} onClose={() => setIsOpen(false)} />
    </div>
  );
};

export default CastRelationshipsPage;

const Header = () => {
  const { t } = useTranslation();
  const handleHistoryBack = useHandleHistoryBack();
  return (
    <header className="flex items-center justify-between px-[16px] py-[8px] relative">
      <button type="button" onClick={handleHistoryBack}>
        <ArrowLeft size={24} className="text-main-dark-1" />
      </button>
      <p className="text-[17px] font-bold leading-[20px] text-main-dark-1 absolute left-1/2 transform -translate-x-1/2">
        {t('CastRelationship.pageTitle')}
      </p>
    </header>
  );
};

const EmptyCase = () => {
  const { t } = useTranslation();
  return (
    <div className="mt-[72px] flex flex-col items-center px-[24px] w-full">
      <div className="flex flex-col items-center gap-[16px]">
        <p className="text-[50px]">😳</p>
        <p className="text-[17px] font-bold leading-[20px] text-main-dark-2">
          {t('CastRelationship.emptyTitle')}
        </p>
      </div>
      <div className="fixed bottom-0 left-0 right-0 px-[24px] pb-[calc(env(safe-area-inset-bottom)+36px)]">
        <Button size="large" className="w-full text-[17px] font-semibold mt-[192px]">
          {t('CastRelationship.emptyStartButton')}
        </Button>
      </div>
    </div>
  );
};

